import { useQuery } from "@apollo/client/react";
import { FC, useState } from "react";

import { styled } from "@mui/material";
import { useLocation } from "react-router-dom";
import { Export, Import, Properties, SourceFilters } from "../../components/Reports";
import { QueryResult } from "../../components/common/QueryResult";
import { SourceBundle, SourceBundlesVolumeDocument } from "../../graphql/types";
import { getQueryParams, toDefaultBytes, toDefaultInt } from "../../lib/Util";
import { ISchedule, ISourceTable } from "./types";

import type { Maybe, SourceTable as SourceTableType } from "../../graphql/types";

const Table = styled("table")`
  margin: 20px;
`;

const Cell = styled("td")(({ theme }) => ({
  border: `1px solid ${theme.palette.surfaceLight50.main}`,
  padding: "3px",
}));

const VolumeTable = styled("table")``;

const VolumeCell = styled("td")`
  border: none;
  padding: 3px;
`;

const Schedule: FC<ISchedule> = ({ bundle, table }) => {
  return (
    <Properties
      descriptors={[bundle.descriptor, table.descriptor, table.parameters]}
      names={[
        "period_hours",
        "hour_of_day",
        "day_of_week",
        "day_of_month",
        "delay_hours",
        "delay_minutes",
        "timezone",
        "repull_days",
      ]}
    />
  );
};

const SourceTableRow = ({ table, bundle }: { table: Maybe<SourceTableType>; bundle: SourceBundle }) => {
  if (!table) return null;

  const volume = table.volume;
  const human_rows = (rows: number) => toDefaultInt(rows);
  const human_bytes = (bytes: number) => toDefaultBytes(bytes);
  return (
    <tr key={table.id}>
      <Cell>
        <Import bundle={bundle} table={table} />
      </Cell>
      <Cell>
        <VolumeTable>
          <tbody>
            <tr>
              <VolumeCell colSpan={2}>
                <b>Rows</b>
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Production</VolumeCell>
              <VolumeCell>
                {human_rows(volume.production_rows)}
                &nbsp; ({human_rows(volume.production_rows / 30)} / day)
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Refills</VolumeCell>
              <VolumeCell>
                {human_rows(volume.refill_rows)}
                &nbsp; ({human_rows(volume.refill_rows / 30)} / day)
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Production + refills</VolumeCell>
              <VolumeCell>
                {human_rows(volume.production_rows + volume.refill_rows)}
                &nbsp; ({human_rows((volume.production_rows + volume.refill_rows) / 30)} / day)
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Backfills</VolumeCell>
              <VolumeCell>{human_rows(volume.backfill_rows)}</VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Tests</VolumeCell>
              <VolumeCell>{human_rows(volume.proof_rows)}</VolumeCell>
            </tr>
            <tr>
              <VolumeCell colSpan={2}>&nbsp;</VolumeCell>
            </tr>
            <tr>
              <VolumeCell colSpan={2}>
                <b>Bytes</b>
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Production</VolumeCell>
              <VolumeCell>
                {human_bytes(volume.production_bytes)}
                &nbsp; ({human_bytes(volume.production_bytes / 30)} / day)
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Refills</VolumeCell>
              <VolumeCell>
                {human_bytes(volume.refill_bytes)}
                &nbsp; ({human_bytes(volume.refill_bytes / 30)} / day)
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Production + refills</VolumeCell>
              <VolumeCell>
                {human_bytes(volume.production_bytes + volume.refill_bytes)}
                &nbsp; ({human_bytes((volume.production_bytes + volume.refill_bytes) / 30)} / day)
              </VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Backfills</VolumeCell>
              <VolumeCell>{human_bytes(volume.backfill_bytes)}</VolumeCell>
            </tr>
            <tr>
              <VolumeCell>Tests</VolumeCell>
              <VolumeCell>{human_bytes(volume.proof_bytes)}</VolumeCell>
            </tr>
          </tbody>
        </VolumeTable>
      </Cell>
      <Cell>
        <Schedule bundle={bundle} table={table} />
      </Cell>
      <Cell>{table.exports.map((e) => (e ? <Export key={e.id} table={e} /> : null))}</Cell>
    </tr>
  );
};

const SourceTable: FC<ISourceTable> = ({ filter }) => {
  const variables = { importName: filter.importName, project: filter.project };
  const { loading, error, data } = useQuery(SourceBundlesVolumeDocument, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-and-network",
    variables,
  });

  return (
    <QueryResult
      data={data}
      error={error}
      loading={loading}
      nodeName="source_bundles"
      emptyElement="No sources"
      resultsMapper={(results: SourceBundle[]) => {
        return (
          <Table>
            <thead>
              <tr>
                <Cell>source</Cell>
                <Cell>payload</Cell>
                <Cell>scheduling</Cell>
                <Cell>destination</Cell>
              </tr>
            </thead>
            <tbody>
              {results.map((bundle) => {
                return bundle.tables.map((table, index) => (
                  <SourceTableRow key={index} table={table} bundle={bundle} />
                ));
              })}
            </tbody>
          </Table>
        );
      }}
    />
  );
};

const SourceReport = () => {
  const location = useLocation();
  const { file, name } = getQueryParams(location.search);

  const [filter, setFilter] = useState<{ importName: string | null; project: string }>({
    importName: name || "",
    project: file || "",
  });

  return (
    <div>
      <SourceFilters {...filter} onChange={setFilter} />
      {filter.project || filter.importName ? <SourceTable filter={filter} /> : <div>Choose a project or import.</div>}
    </div>
  );
};

export { SourceReport };
